import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";

import { useListOrganizationProviders } from "~/admins/hooks/organizations";
import { CancelSubmitButtons, LoadingState } from "~/common/components";
import Modal from "~/common/components/Modal";
import Select from "~/common/components/ui/select/Select";
import TextArea from "~/common/components/ui/Textarea";
import { TEXT_MAX_LENGTH } from "~/common/constants";
import { Option } from "~/common/utils";
import { hasAccountConfigured } from "~/patients/utils/hasAccountConfigured";
import { useAdmitPatient, useListCarePlans } from "~/providers/hooks";
import {
  AdmitPatientFormValues,
  admitPatientSchema,
  generateCarePlanOptions,
  PatientData,
} from "~/providers/utils";
import { CarePlansWarning } from "../../carePlans/CarePlansWarning";

interface AdmitPatientModalProps {
  show: boolean;
  onClose: () => void;
  patient: PatientData;
}

export const AdmitPatientModal = ({
  onClose,
  show,
  patient,
}: AdmitPatientModalProps) => {
  const orgPatient = patient.organization_patients[0];
  const { data: carePlansList, isLoading: isLoadingCarePlans } =
    useListCarePlans({
      withPatients: false,
    });

  const { data: providers } = useListOrganizationProviders(
    orgPatient.organization_id,
  );

  const primaryProvider = orgPatient.primary_provider;

  const activatedProviders = providers?.filter(
    (provider) => !provider.organization_team_members[0].deactivated_at,
  );

  const providersWithConfiguredAccount = activatedProviders?.filter(
    (provider) => hasAccountConfigured(provider),
  );

  const providerOptions: Option[] = (providersWithConfiguredAccount ?? []).map(
    (provider) => ({
      value: provider.id,
      label: `${provider.name} ${provider.last_name}`,
    }),
  );

  const defaultCarePlan = carePlansList?.find((carePlan) => carePlan.default);
  const {
    control,
    reset,
    handleSubmit,
    register,
    watch,
    formState: { errors },
  } = useForm<AdmitPatientFormValues>({
    resolver: zodResolver(admitPatientSchema),
    values: {
      primary_provider_id: primaryProvider?.id ?? "",
      provider_care_plan_id: defaultCarePlan?.id ?? "",
      internal_note: "",
    },
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  const { id: patientId, consultation } = patient;
  const consultationId = consultation?.id;
  const { admitPatientMutation, isPending } = useAdmitPatient(
    patientId,
    handleClose,
  );

  const onSubmit = (admitPatientData: AdmitPatientFormValues) =>
    consultationId &&
    admitPatientMutation({ consultationId, admitPatientData });

  const hasNoCarePlans = !carePlansList?.length;

  return (
    <Modal title="Admit patient" show={show} onClose={handleClose}>
      {isLoadingCarePlans ? (
        <LoadingState />
      ) : (
        <form
          className="flex size-full flex-col gap-4"
          onSubmit={handleSubmit(onSubmit)}
        >
          <Select
            id="primary_provider_id"
            label="Primary provider"
            placeholder="Select provider"
            options={providerOptions}
            control={control}
            error={errors.primary_provider_id?.message}
          />
          <Select
            id="provider_care_plan_id"
            error={errors.provider_care_plan_id?.message}
            placeholder="Select plan"
            label="Care plan"
            control={control}
            options={generateCarePlanOptions(carePlansList ?? [])}
            isDisabled={hasNoCarePlans}
          />
          {hasNoCarePlans && <CarePlansWarning />}
          <TextArea
            label="Internal note (optional)"
            placeholder=""
            rows={4}
            charactersCount={watch("internal_note")?.length}
            maxCharacters={TEXT_MAX_LENGTH}
            error={errors.internal_note?.message}
            {...register("internal_note")}
          />
          <CancelSubmitButtons
            containerClassName="flex justify-end"
            cancelClassName="w-fit"
            submitClassName="w-fit"
            handleCancel={handleClose}
            cancelButtonLabel="Cancel"
            submitButtonLabel="Admit"
            isSubmitting={isPending}
          />
        </form>
      )}
    </Modal>
  );
};
